import Splide from '@splidejs/splide';

/* Interfaces */
interface IAriaTranslations {
    prev: string;
    next: string;
    first: string;
    last: string;
    slideX: string;
    carousel: string;
    select: string;
    slide: string;
    slideLabel: string;
}

interface ICarouselOptions {
    type: string;
    speed: number;
    width: string;
    fixedWidth?: string;
    start: number;
    focus: string;
    perPage: number;
    perMove: number;
    easing: string;
    drag: boolean;
    flickPower: number;
    flickMaxPages: number;
    keyboard: boolean;
    updateOnMove: boolean;
    mediaQuery: string;
    breakpoints?: object;
    i18n?: IAriaTranslations;
}

/* HTML elements */
const carouselSections: NodeListOf<HTMLElement> = document.querySelectorAll('.carousel-block__carousel');

/* Global variables */
let hasOpenModal: boolean = false;
let ariaTranslations: IAriaTranslations | undefined = undefined;

/* Splide base options */
let carouselBaseOptions: ICarouselOptions = {
    type: 'loop',
    speed: 500,
    width: '100%',
    start: 0,
    focus: 'center',
    perPage: 3,
    perMove: 1,
    easing: 'ease',
    drag: true,
    flickPower: 150,
    flickMaxPages: 1,
    keyboard: true,
    updateOnMove: true,
    mediaQuery: 'min',
}

/* Functions */
const mountCarousel = (
    carouselId: string,
    currentSlide: number | null = null,
    isModalCarousel: boolean = false
): Splide => {
    // Set current slide
    if (currentSlide != null) {
        carouselBaseOptions.start = currentSlide;
    }
    
    // Set carousel options
    let carouselOptions: ICarouselOptions | any = carouselBaseOptions;

    if (isModalCarousel) {
        carouselOptions.width = '1508px';
        carouselOptions.perPage = 1;
    } else {
        carouselOptions = {
            ...carouselOptions,
            fixedWidth: '326px',
            breakpoints: {
                576: {
                    fixedWidth: '456px',
                },
                768: {
                    fixedWidth: '546px',
                },
            },
        }
    }
    
    // Create  and mount Splide
    const carousel: Splide = new Splide(`#${carouselId}`, carouselOptions);
    
    carousel.mount();

    return carousel;
}

const openCarouselModal = (modal: HTMLDialogElement, carousel: Splide): Splide => {
    modal.showModal();
    modal.focus();
    
    const currentSlide: number = carousel.index;

    document.body.classList.add('has-full-screen-modal');
    
    const carouselSection = modal.querySelector('.carousel-block__carousel-in-modal') as HTMLElement;
    const carouselId: string = carouselSection.id;
    
    let carouselInModal: Splide = mountCarousel(carouselId, currentSlide, true);
    
    
    
    hasOpenModal = true;
    
    return carouselInModal;
}

const closeCarouselModal = (modal: HTMLDialogElement, carouselInModal: Splide, carousel: Splide): Splide => {
    const currentSlide: number = carouselInModal.index;
    
    carouselInModal.destroy();
    
    document.body.classList.remove('has-full-screen-modal');

    const carouselSection = modal.previousElementSibling as HTMLElement;
    const carouselId: string = carouselSection.id;
    
    carousel.destroy();
    let newCarousel: Splide = mountCarousel(carouselId, currentSlide);
    
    hasOpenModal = false;
    
    return newCarousel;
}

const setAriaTranslations = (carouselSection: HTMLElement): void => {
    let ariaTranslationsString = carouselSection.dataset.ariaText as string;
    let ariaTranslationsArray = ariaTranslationsString.split(', ') as string[];
    
    let ariaTranslationsObject: IAriaTranslations = {
        prev: ariaTranslationsArray[0],
        next: ariaTranslationsArray[1],
        first: ariaTranslationsArray[2],
        last: ariaTranslationsArray[3],
        slideX: ariaTranslationsArray[4],
        carousel: ariaTranslationsArray[5],
        select: ariaTranslationsArray[6],
        slide: ariaTranslationsArray[7],
        slideLabel: ariaTranslationsArray[8]
    }

    ariaTranslations = ariaTranslationsObject;
}

const setUpCarousel = (carouselSection: HTMLElement): void => {
    // Set aria translations
    if (!ariaTranslations) {
        setAriaTranslations(carouselSection);
        carouselBaseOptions.i18n = ariaTranslations;
    }
    
    // Mount main carousel
    const carouselId: string = carouselSection.id;
    let carousel: Splide = mountCarousel(carouselId);
    
    let carouselInModal: Splide | null = null;
    
    const carouselModal = carouselSection.nextElementSibling as HTMLDialogElement;
    
    // Listen to events
    const openCarouselModalButton = carouselSection.querySelector('.carousel-block__open-modal-button') as HTMLButtonElement;
    openCarouselModalButton.addEventListener('click', (): void => {
        if (!hasOpenModal) {
            carouselInModal = openCarouselModal(carouselModal, carousel);
        }
    });
    
    const closeCarouselModalButton = carouselModal.querySelector('.carousel-block__close-modal-button') as HTMLButtonElement;
    
    closeCarouselModalButton.addEventListener('click', (): void => {
        carouselModal.close();
    });
    
    carouselModal.addEventListener('click', (event: MouseEvent) => {
        if (event.target instanceof Element && event.target === carouselModal) {
            carouselModal.close();
        }
    });
    
    carouselModal.addEventListener('close', (): void => {
        if (hasOpenModal) {
            carousel = closeCarouselModal(carouselModal, carouselInModal!, carousel);
        }
        carouselInModal = null;
    });
}

/* Listen to DOM content being loaded */
document.addEventListener( 'DOMContentLoaded', (): void => {
    if (carouselSections.length > 0) {
        carouselSections.forEach(carouselSection => {
            setUpCarousel(carouselSection);
        });
    }
});
